import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@shared/lib/supabase';
import { MessageSquare, Bug, Lightbulb, HelpCircle, AlertCircle, CheckCircle, Clock, XCircle, Filter, Search, Loader, ChevronDown, ChevronUp, ArrowLeft } from 'lucide-react';
import { Button } from '@shared/components/ui/Button';
import { Input } from '@shared/components/ui/Input';
import { toast } from '@shared/components/ui/Toaster';

interface FeedbackSubmission {
  id: string;
  user_id: string;
  feedback_type: 'bug_report' | 'feature_request' | 'general_feedback' | 'other';
  severity: 'low' | 'medium' | 'high' | 'critical';
  subject: string;
  description: string;
  page_url: string;
  user_email: string;
  status: 'new' | 'in_progress' | 'resolved' | 'closed';
  admin_notes: string | null;
  admin_response: string | null;
  resolved_at: string | null;
  resolved_by: string | null;
  created_at: string;
  updated_at: string;
  profiles?: {
    full_name: string;
  };
}

const AdminFeedback: React.FC = () => {
  const navigate = useNavigate();
  const [feedback, setFeedback] = useState<FeedbackSubmission[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [typeFilter, setTypeFilter] = useState<string>('all');
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [adminNotes, setAdminNotes] = useState('');
  const [adminResponse, setAdminResponse] = useState('');
  const [selectedStatus, setSelectedStatus] = useState<string>('');
  const [updating, setUpdating] = useState(false);

  useEffect(() => {
    loadFeedback();
  }, []);

  const loadFeedback = async () => {
    try {
      setLoading(true);
      setError(null);

      // Query feedback submissions
      const { data: feedbackData, error: feedbackError } = await supabase
        .from('feedback_submissions')
        .select('*')
        .order('created_at', { ascending: false });

      if (feedbackError) {
        console.error('Error loading feedback:', feedbackError);
        throw feedbackError;
      }

      // Get unique user IDs
      const userIds = [...new Set(feedbackData?.map(f => f.user_id) || [])];

      // Fetch profiles for these users
      const { data: profilesData, error: profilesError } = await supabase
        .from('profiles')
        .select('id, full_name')
        .in('id', userIds);

      if (profilesError) {
        console.error('Error loading profiles:', profilesError);
        // Continue without profiles rather than failing completely
      }

      // Create a map of user_id to profile
      const profilesMap = new Map(
        profilesData?.map(p => [p.id, p]) || []
      );

      // Merge the data
      const mergedData = feedbackData?.map(feedback => ({
        ...feedback,
        profiles: profilesMap.get(feedback.user_id)
      })) || [];

      setFeedback(mergedData);
    } catch (error: any) {
      console.error('Error loading feedback:', error);
      const errorMessage = error.message || 'Failed to load feedback submissions';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateFeedback = async (feedbackId: string) => {
    try {
      setUpdating(true);

      const updates: any = {
        updated_at: new Date().toISOString()
      };

      if (adminNotes.trim()) {
        updates.admin_notes = adminNotes.trim();
      }

      if (adminResponse.trim()) {
        updates.admin_response = adminResponse.trim();
      }

      if (selectedStatus) {
        updates.status = selectedStatus;
        if (selectedStatus === 'resolved' || selectedStatus === 'closed') {
          const { data: { user } } = await supabase.auth.getUser();
          updates.resolved_at = new Date().toISOString();
          updates.resolved_by = user?.id;
        }
      }

      const { error } = await supabase
        .from('feedback_submissions')
        .update(updates)
        .eq('id', feedbackId);

      if (error) throw error;

      toast.success('Feedback updated successfully');
      setEditingId(null);
      setAdminNotes('');
      setAdminResponse('');
      setSelectedStatus('');
      loadFeedback();
    } catch (error: any) {
      console.error('Error updating feedback:', error);
      toast.error('Failed to update feedback');
    } finally {
      setUpdating(false);
    }
  };

  const startEditing = (submission: FeedbackSubmission) => {
    setEditingId(submission.id);
    setAdminNotes(submission.admin_notes || '');
    setAdminResponse(submission.admin_response || '');
    setSelectedStatus(submission.status);
    setExpandedId(submission.id);
  };

  const cancelEditing = () => {
    setEditingId(null);
    setAdminNotes('');
    setAdminResponse('');
    setSelectedStatus('');
  };

  const getFeedbackTypeIcon = (type: string) => {
    switch (type) {
      case 'bug_report':
        return <Bug className="w-5 h-5 text-red-600" />;
      case 'feature_request':
        return <Lightbulb className="w-5 h-5 text-yellow-600" />;
      case 'general_feedback':
        return <MessageSquare className="w-5 h-5 text-blue-600" />;
      case 'other':
        return <HelpCircle className="w-5 h-5 text-gray-600" />;
      default:
        return <MessageSquare className="w-5 h-5 text-gray-600" />;
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'new':
        return <AlertCircle className="w-5 h-5 text-blue-600" />;
      case 'in_progress':
        return <Clock className="w-5 h-5 text-yellow-600" />;
      case 'resolved':
        return <CheckCircle className="w-5 h-5 text-green-600" />;
      case 'closed':
        return <XCircle className="w-5 h-5 text-gray-600" />;
      default:
        return <AlertCircle className="w-5 h-5 text-gray-600" />;
    }
  };

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'critical':
        return 'bg-red-100 text-red-800 border-red-300';
      case 'high':
        return 'bg-orange-100 text-orange-800 border-orange-300';
      case 'medium':
        return 'bg-yellow-100 text-yellow-800 border-yellow-300';
      case 'low':
        return 'bg-blue-100 text-blue-800 border-blue-300';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-300';
    }
  };

  const filteredFeedback = feedback.filter((item) => {
    const matchesSearch =
      searchTerm === '' ||
      item.subject.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.profiles?.full_name?.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesStatus = statusFilter === 'all' || item.status === statusFilter;
    const matchesType = typeFilter === 'all' || item.feedback_type === typeFilter;

    return matchesSearch && matchesStatus && matchesType;
  });

  const stats = {
    total: feedback.length,
    new: feedback.filter((f) => f.status === 'new').length,
    in_progress: feedback.filter((f) => f.status === 'in_progress').length,
    resolved: feedback.filter((f) => f.status === 'resolved').length,
    bugs: feedback.filter((f) => f.feedback_type === 'bug_report').length,
    features: feedback.filter((f) => f.feedback_type === 'feature_request').length
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 mb-2">User Feedback & Issues</h1>
              <p className="text-gray-600">Manage and respond to user feedback and issue reports</p>
            </div>
            <Button
              variant="outline"
              onClick={() => navigate('/admin')}
              className="flex items-center gap-2"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Admin Dashboard
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-6 gap-4 mb-8">
          <div className="bg-white rounded-lg shadow p-4">
            <p className="text-sm text-gray-600">Total</p>
            <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <p className="text-sm text-gray-600">New</p>
            <p className="text-2xl font-bold text-blue-600">{stats.new}</p>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <p className="text-sm text-gray-600">In Progress</p>
            <p className="text-2xl font-bold text-yellow-600">{stats.in_progress}</p>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <p className="text-sm text-gray-600">Resolved</p>
            <p className="text-2xl font-bold text-green-600">{stats.resolved}</p>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <p className="text-sm text-gray-600">Bugs</p>
            <p className="text-2xl font-bold text-red-600">{stats.bugs}</p>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <p className="text-sm text-gray-600">Features</p>
            <p className="text-2xl font-bold text-yellow-600">{stats.features}</p>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow mb-6 p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <Input
                type="text"
                placeholder="Search feedback..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="all">All Status</option>
              <option value="new">New</option>
              <option value="in_progress">In Progress</option>
              <option value="resolved">Resolved</option>
              <option value="closed">Closed</option>
            </select>
            <select
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="all">All Types</option>
              <option value="bug_report">Bug Reports</option>
              <option value="feature_request">Feature Requests</option>
              <option value="general_feedback">General Feedback</option>
              <option value="other">Other</option>
            </select>
          </div>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-6 mb-6">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-6 h-6 text-red-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-red-900 mb-2">Error Loading Feedback</h3>
                <p className="text-red-700 mb-4">{error}</p>
                <Button
                  onClick={loadFeedback}
                  className="bg-red-600 hover:bg-red-700"
                >
                  Retry
                </Button>
              </div>
            </div>
          </div>
        )}

        <div className="space-y-4">
          {filteredFeedback.length === 0 && !error ? (
            <div className="bg-white rounded-lg shadow p-8 text-center">
              <MessageSquare className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600">No feedback submissions found</p>
            </div>
          ) : !error && (
            filteredFeedback.map((submission) => (
              <div key={submission.id} className="bg-white rounded-lg shadow overflow-hidden">
                <div
                  className="p-6 cursor-pointer hover:bg-gray-50"
                  onClick={() => setExpandedId(expandedId === submission.id ? null : submission.id)}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-4 flex-1">
                      {getFeedbackTypeIcon(submission.feedback_type)}
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <h3 className="text-lg font-semibold text-gray-900">
                            {submission.subject}
                          </h3>
                          {submission.feedback_type === 'bug_report' && (
                            <span className={`px-2 py-1 text-xs font-medium rounded border ${getSeverityColor(submission.severity)}`}>
                              {submission.severity.toUpperCase()}
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-4 text-sm text-gray-600">
                          <span className="flex items-center gap-1">
                            {getStatusIcon(submission.status)}
                            {submission.status.replace('_', ' ').toUpperCase()}
                          </span>
                          <span>{submission.profiles?.full_name || 'Unknown User'}</span>
                          <span>{new Date(submission.created_at).toLocaleDateString()}</span>
                        </div>
                      </div>
                    </div>
                    <button className="text-gray-400 hover:text-gray-600">
                      {expandedId === submission.id ? (
                        <ChevronUp className="w-5 h-5" />
                      ) : (
                        <ChevronDown className="w-5 h-5" />
                      )}
                    </button>
                  </div>
                </div>

                {expandedId === submission.id && (
                  <div className="border-t border-gray-200 p-6 bg-gray-50">
                    <div className="space-y-4">
                      <div>
                        <h4 className="font-medium text-gray-900 mb-2">Description</h4>
                        <p className="text-gray-700 whitespace-pre-wrap">{submission.description}</p>
                      </div>

                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div>
                          <h4 className="font-medium text-gray-900 mb-1">Contact Email</h4>
                          <p className="text-gray-700">{submission.user_email || 'Not provided'}</p>
                        </div>
                        <div>
                          <h4 className="font-medium text-gray-900 mb-1">Page URL</h4>
                          <p className="text-gray-700 truncate">{submission.page_url || 'Not captured'}</p>
                        </div>
                      </div>

                      {editingId === submission.id ? (
                        <div className="space-y-4 pt-4 border-t border-gray-300">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Status
                            </label>
                            <select
                              value={selectedStatus}
                              onChange={(e) => setSelectedStatus(e.target.value)}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            >
                              <option value="new">New</option>
                              <option value="in_progress">In Progress</option>
                              <option value="resolved">Resolved</option>
                              <option value="closed">Closed</option>
                            </select>
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Admin Notes (Internal)
                            </label>
                            <textarea
                              value={adminNotes}
                              onChange={(e) => setAdminNotes(e.target.value)}
                              rows={3}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                              placeholder="Internal notes for the team..."
                            />
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Response to User
                            </label>
                            <textarea
                              value={adminResponse}
                              onChange={(e) => setAdminResponse(e.target.value)}
                              rows={3}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                              placeholder="Response that will be visible to the user..."
                            />
                          </div>

                          <div className="flex justify-end gap-3">
                            <Button
                              variant="outline"
                              onClick={cancelEditing}
                              disabled={updating}
                            >
                              Cancel
                            </Button>
                            <Button
                              onClick={() => handleUpdateFeedback(submission.id)}
                              disabled={updating}
                            >
                              {updating ? 'Updating...' : 'Update'}
                            </Button>
                          </div>
                        </div>
                      ) : (
                        <>
                          {(submission.admin_notes || submission.admin_response) && (
                            <div className="space-y-3 pt-4 border-t border-gray-300">
                              {submission.admin_notes && (
                                <div>
                                  <h4 className="font-medium text-gray-900 mb-1">Admin Notes</h4>
                                  <p className="text-gray-700 whitespace-pre-wrap">{submission.admin_notes}</p>
                                </div>
                              )}
                              {submission.admin_response && (
                                <div>
                                  <h4 className="font-medium text-gray-900 mb-1">Response to User</h4>
                                  <p className="text-gray-700 whitespace-pre-wrap">{submission.admin_response}</p>
                                </div>
                              )}
                            </div>
                          )}

                          <div className="flex justify-end pt-4 border-t border-gray-300">
                            <Button onClick={() => startEditing(submission)}>
                              Manage Feedback
                            </Button>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminFeedback;
